import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import * as StoreActions from '../../actions/stores'
import * as DeviceActions from '../../actions/device'
import { colors, Routes } from '../../constants'
import { isDeviceOnline, selectStoreWithDevices } from '../../reducers/stores'
import { usePageTitle } from '../../actions/app'
import { Loader } from '../../components'

const ESTATES_WITH_GLOBAL_SELECTION = ['PRE', 'PLD', 'FRA']
const ALL_STORES_VALUE = '__ALL_STORES__'
const ALL_LOCATIONS_VALUE = '__ALL_LOCATIONS__'

const Page = styled.div`
    padding: 30px;
    max-width: 1100px;
    margin: 0 auto;
`

const Card = styled.div`
    background: ${colors.white};
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 20px;
    margin-bottom: 16px;
`

const SectionTitle = styled.h2`
    margin: 0 0 12px 0;
    font-size: 20px;
    font-weight: 600;
`

const Row = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
`

const Column = styled.div`
    flex: 1;
    min-width: 260px;
`

const Label = styled.div`
    font-weight: 600;
    margin-bottom: 6px;
    color: ${colors.grey};
`

const ActionsList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px;
`

const ActionButton = styled.button`
    border: 1px solid ${props => (props.active ? colors.blue : colors.greylight)};
    border-radius: 6px;
    background: ${props => (props.active ? colors.bluelight : colors.white)};
    color: ${colors.bluedark};
    padding: 10px 12px;
    cursor: pointer;
    text-align: left;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${colors.blue};
    }
`

const RunButton = styled.button`
    background: ${colors.blue};
    color: ${colors.white};
    border: none;
    border-radius: 6px;
    padding: 12px 18px;
    font-weight: 600;
    cursor: pointer;
    min-width: 160px;
    opacity: ${props => (props.disabled ? 0.6 : 1)};
    pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
`

const ProgressBar = styled.div`
    width: 100%;
    height: 12px;
    background: ${colors.greylightest};
    border-radius: 20px;
    overflow: hidden;
`

const ProgressFill = styled.div`
    height: 100%;
    background: ${colors.blue};
    width: ${props => props.percent}%;
    transition: width 0.2s ease;
`

const Warning = styled.div`
    background: ${colors.bluelight};
    color: ${colors.white};
    padding: 12px 14px;
    border-radius: 6px;
    font-weight: 500;
    margin-bottom: 10px;
`

const Section = styled(Card)`
    margin-top: 12px;
`


const OperationsHub = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const [selectedStoreCode, setSelectedStoreCode] = useState(null)
    const [selectedLocationId, setSelectedLocationId] = useState(null)
    const [selectedAction, setSelectedAction] = useState(null)
    const [isRunning, setIsRunning] = useState(false)
    const [completed, setCompleted] = useState(0)

    const storeList = useSelector((state) => state.stores.list)
    const storeLocations = useSelector((state) => state.device.storeLocations)
    const deviceList = useSelector((state) => state.device.list)
    const deviceStatusMap = useSelector((state) => state.stores.devices)
    const selectedStore = useSelector(selectStoreWithDevices)
    const userEmail = useSelector((state) => state.user?.session?.email)
    const selectedEstate = useSelector((state) => state.app?.selectedEstate)
    const allowGlobalSelection = ESTATES_WITH_GLOBAL_SELECTION.includes(selectedEstate)
    const isAllStoresSelected = allowGlobalSelection && selectedStoreCode === ALL_STORES_VALUE

    usePageTitle('Operations Hub')

    // Fetch stores (installed devices only) and store locations once
    useEffect(() => {
        if (!storeList.length) {
            dispatch(
                StoreActions.fetchStoresPaginated({
                    paginated: 0,
                    showEmptyStores: true,
                })
            )
            dispatch(StoreActions.fetchStoresAndDevicesStatus(''))
        }
    }, [dispatch, selectedEstate, storeList.length])

    useEffect(() => {
        if (!deviceList.length) {
            dispatch(DeviceActions.fetchDevices())
        }
    }, [deviceList.length, dispatch])

    useEffect(() => {
        if (!storeLocations.length) {
            dispatch(DeviceActions.fetchStoreLocations())
        }
    }, [dispatch, storeLocations.length])

    // Load store detail + installed devices when selection changes
    useEffect(() => {
        if (selectedStoreCode && !isAllStoresSelected) {
            dispatch(
                StoreActions.fetchSingleStore({
                    storeCode: selectedStoreCode,
                    deviceGroups: [1],
                })
            )
        }
    }, [dispatch, isAllStoresSelected, selectedStoreCode])

    const storeOptions = useMemo(
        () => {
            let options = storeList.map(({ code, name }) => ({
                value: code,
                label: `${name || code} (${code})`,
            }))

            if (!options.length) {
                const storeCodes = [...new Set((deviceList || []).map((device) => device?.store_code).filter(Boolean))].sort()
                options = storeCodes.map((code) => ({
                    value: code,
                    label: code,
                }))
            }

            if (allowGlobalSelection) {
                options.push({ value: ALL_STORES_VALUE, label: t('All Stores') })
            }

            return options
        },
        [allowGlobalSelection, deviceList, storeList, t]
    )

    const locationOptions = useMemo(() => {
        const devices = isAllStoresSelected ? deviceList : (selectedStore?.devices || [])
        const seen = {}
        const options = devices
            .filter((d) => Number(d.display_group) === 1)
            .map((device) => device.store_location)
            .filter((loc) => loc && typeof loc.id !== 'undefined')
            .filter((loc) => {
                if (seen[loc.id]) {
                    return false
                }
                seen[loc.id] = true
                return true
            })
            .map((loc) => ({
                value: Number(loc.id),
                label: loc.name || loc.slug || `Location ${loc.id}`,
            }))

        if (allowGlobalSelection) {
            options.unshift({ value: ALL_LOCATIONS_VALUE, label: t('All Locations') })
        }

        return options
    }, [allowGlobalSelection, deviceList, isAllStoresSelected, selectedStore, t])

    const devices = useMemo(() => {
        if (!selectedStoreCode) {
            return []
        }

        let allDevices = []
        if (isAllStoresSelected) {
            allDevices = deviceList || []
        } else if (selectedStore?.code === selectedStoreCode) {
            allDevices = selectedStore?.devices || []
        }

        return allDevices
            .filter((d) => Number(d.display_group) === 1)
            .filter((d) => {
                if (!selectedLocationId || (allowGlobalSelection && selectedLocationId === ALL_LOCATIONS_VALUE)) {
                    return true
                }
                return Number(d.store_location?.id) === Number(selectedLocationId)
            })
    }, [allowGlobalSelection, deviceList, isAllStoresSelected, selectedLocationId, selectedStore, selectedStoreCode])

    const totalDevices = devices.length
    const progressPercent = totalDevices ? Math.min((completed / totalDevices) * 100, 100) : 0

    useEffect(() => {
        setCompleted(0)
        setIsRunning(false)
    }, [selectedStoreCode, selectedLocationId])

    useEffect(() => {
        if (allowGlobalSelection) {
            return
        }

        if (selectedStoreCode === ALL_STORES_VALUE) {
            setSelectedStoreCode(null)
            dispatch(StoreActions.deselectStore())
        }

        if (selectedLocationId === ALL_LOCATIONS_VALUE) {
            setSelectedLocationId(null)
        }
    }, [allowGlobalSelection, dispatch, selectedLocationId, selectedStoreCode])


    const actions = React.useMemo(
        () => [
            { key: 'force-download', label: 'Force Download Content', dispatcher: DeviceActions.updateContentViaApi },
            { key: 'force-settings', label: 'Force Settings Push', dispatcher: DeviceActions.updateSettingsViaApi },
            { key: 'refresh-screenshots', label: 'Refresh Screenshots', dispatcher: DeviceActions.demandScreenshotViaApi },
            { key: 'restart-app', label: 'Restart Volume App', dispatcher: DeviceActions.restartDeviceViaApi },
            { key: 'clear-cache', label: 'Clear Cache', dispatcher: DeviceActions.clearDeviceCacheViaApi },
            { key: 'clear-content', label: 'Clear Content', dispatcher: DeviceActions.clearDeviceContentViaApi },
            { key: 'reboot', label: 'Reboot Devices', dispatcher: DeviceActions.rebootDeviceViaApi },
            { key: 'open-settings-panel', label: 'Open Settings Panel', dispatcher: DeviceActions.openStatusPanelViaAPI },
        ],
        []
    )

    const onRun = useCallback(() => {
        if (!selectedAction || !totalDevices || isRunning) {
            return
        }

        const action = actions.find(({ key }) => key === selectedAction)
        if (!action) {
            return
        }

        setIsRunning(true)
        setCompleted(0)

        let index = 0

        const sendNext = () => {
            if (index >= devices.length) {
                setIsRunning(false)
                return
            }

            const device = devices[index]
            if (device?.id) {
                dispatch(action.dispatcher(device.id))
            }

            index += 1
            setCompleted(index)

            if (index < devices.length) {
                setTimeout(sendNext, 500)
            } else {
                setTimeout(() => setIsRunning(false), 0)
            }
        }

        sendNext()
    }, [actions, devices, dispatch, isRunning, selectedAction, totalDevices])

    const isInternalUser = useMemo(() => /@volumenetwork\.com$/i.test(userEmail || ''), [userEmail])

    if (!isInternalUser) {
        return (
            <Page>
                <Card>
                    <SectionTitle>Operations Hub</SectionTitle>
                    <Warning>{t('You need admin access to view this page.')}</Warning>
                </Card>
            </Page>
        )
    }

    return (
        <Page>
            <Card>
                <SectionTitle>Operations Hub</SectionTitle>
                <Warning>{t('Keep this page open while commands are sent.')}</Warning>
                <Row>
                    <Column>
                        <Label>{t('Store')}</Label>
                        <Select
                            options={storeOptions}
                            value={storeOptions.find((opt) => opt.value === selectedStoreCode) || null}
                            onChange={(opt) => {
                                const code = opt?.value || null
                                setSelectedStoreCode(code)
                                setSelectedLocationId(null)
                                if (!code || code === ALL_STORES_VALUE) {
                                    dispatch(StoreActions.deselectStore())
                                }
                            }}
                            placeholder={t('Select store')}
                            isClearable
                        />
                    </Column>
                    <Column>
                        <Label>{t('Device location')}</Label>
                        <Select
                            options={locationOptions}
                            value={locationOptions.find((opt) => opt.value === selectedLocationId) || null}
                            onChange={(opt) => setSelectedLocationId(opt?.value || null)}
                            placeholder={t('All locations')}
                            isClearable
                            isDisabled={!selectedStoreCode}
                        />
                    </Column>
                </Row>
            </Card>

            <Card>
                <SectionTitle>{t('Actions')}</SectionTitle>
                <ActionsList>
                    {actions.map((action) => (
                        <ActionButton
                            key={action.key}
                            type="button"
                            active={selectedAction === action.key}
                            onClick={() => setSelectedAction(action.key)}
                        >
                            {action.label}
                        </ActionButton>
                    ))}
                </ActionsList>
            </Card>

            <Card>
                <SectionTitle>{t('Run')}</SectionTitle>
                <Row>
                    <Column>
                        <Label>{t('Devices to target')}</Label>
                        <div style={{ fontSize: '28px', fontWeight: 700 }}>{totalDevices}</div>
                    </Column>
                    <Column style={{ alignSelf: 'center', textAlign: 'right' }}>
                        <RunButton onClick={onRun} disabled={!selectedAction || !totalDevices || isRunning}>
                            {isRunning ? t('Running...') : t('Run')}
                        </RunButton>
                    </Column>
                </Row>
                <div style={{ marginTop: '16px', marginBottom: '8px', fontWeight: 600 }}>
                    {t('Progress')}: {completed}/{totalDevices}
                </div>
                <ProgressBar>
                    <ProgressFill percent={progressPercent} />
                </ProgressBar>
            </Card>

        </Page>
    )
}

export default OperationsHub
